import { useDebounceFn } from 'ahooks';
import type { SearchableListProps } from '@archipro-design/aria';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import {
    Checkbox,
    SearchableList,
    pxToRem,
    useStyles,
} from '@archipro-design/aria';
import * as S from './FilterSearchList.style';
import type {
    FilterStates,
    FilterStatesKey,
    SearchListKeys,
} from '../../type/filter-state';
import type { SearchAggregationParams, SearchIndex } from '../../type/api';
import { SEARCH_LIST_AGG_PARAM_KEY_MAP } from '../../config/search-filter';
import type {
    AggregateRequest,
    AggregateState,
} from '../../hook/use-aggregation';
import { useEffect, useRef, useState } from 'react';
import { compareSearchListLabels } from '../../util/compare-search-list-labels';

export interface FilterSearchListItem {
    label: string;
    value: string;
    checked?: boolean;
}

export interface SearchListFilter {
    kind: 'SearchList';
    filterKey: FilterStatesKey;
    /** The facet ID for the facet filters. */
    subFilterKey?: string;
    props: SearchableListProps<FilterSearchListItem> & { hideSearch?: boolean };
    // filter bar will display if it is true
    displayOnFilterBar?: boolean;
    filterBarOrder?: number;
    filterBarTitle?: string;
    displayOnMobileFilterBar?: boolean;
}

export interface SearchListFilterState extends Pick<SearchListFilter, 'kind'> {
    // this is listed items, with search query it might not contain the selected items
    items: FilterSearchListItem[];
    selectedItems?: FilterSearchListItem[];
    search?: string;
    after?: Record<string, unknown>;
    hasMoreItems?: boolean;
    rawSort?: boolean;
}

const DEFAULT_LOAD_SIZE = 40;

interface FilterSearchListProps {
    category?: SearchIndex;
    filterStates: FilterStates;
    filter: SearchListFilter;
    state: SearchListFilterState;
    updateFilter: (
        filterKey: FilterStatesKey,
        state: Partial<SearchListFilterState>,
        updateStates?: boolean,
        subFilterKey?: string,
    ) => void;
    aggregate: (request: AggregateRequest) => void;
    disabled?: boolean;
}

const FilterSearchList = ({
    filterStates,
    filter,
    state,
    updateFilter,
    aggregate,
    disabled,
}: FilterSearchListProps) => {
    const { props, filterKey, subFilterKey } = filter;
    const { hideSearch, ...restProps } = props;
    const { items, search, hasMoreItems, after, selectedItems, rawSort } =
        state;
    const [loading, setLoading] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    // Draft while typing so we do not write `search` into parent state on
    // every keystroke. That re-renders SearchableList / VirtualList and iOS
    // dismisses the keyboard.
    const [draftSearch, setDraftSearch] = useState(search ?? '');
    const { css } = useStyles({ emptyList: items.length === 0, hideSearch });
    const listRef = useRef<HTMLDivElement>(null);
    const { desktop } = useAppDisplayModeContext();
    const itemsRef = useRef(items);
    itemsRef.current = items;
    // Bumped on each new search so a load-more that was already queued
    // cannot write the pre-search list over the new results.
    const searchGenerationRef = useRef(0);
    const searchInFlightRef = useRef(false);

    const handleAggregateStateChange = (
        request: AggregateRequest,
        state: AggregateState,
        data: FilterStates | undefined,
        generation: number,
    ) => {
        if (state !== 'loaded') return;
        if (generation !== searchGenerationRef.current) return;
        searchInFlightRef.current = false;
        setLoading(false);
        const newFilterState = data?.[filterKey];
        let newListState: SearchListFilterState | null = null;
        if (Array.isArray(newFilterState)) {
            newFilterState.forEach((singleFilter) => {
                if (
                    typeof singleFilter !== 'number' &&
                    singleFilter.id === subFilterKey
                ) {
                    newListState = singleFilter.state;
                }
            });
        } else if (newFilterState?.kind === 'SearchList') {
            newListState = newFilterState;
        }
        if (!newListState) {
            if (request.aggregateType === 'searchListLoadMore') {
                updateFilter(
                    filterKey,
                    { hasMoreItems: false, after: undefined },
                    false,
                    subFilterKey,
                );
            } else {
                updateFilter(
                    filterKey,
                    {
                        items: [],
                        hasMoreItems: false,
                        after: undefined,
                    },
                    false,
                    subFilterKey,
                );
            }
            return;
        }

        let newUpdateState: Partial<SearchListFilterState>;
        let newItems: FilterSearchListItem[];
        const currentItems = itemsRef.current;
        if (request.aggregateType === 'searchListLoadMore') {
            if (!newListState || newListState?.items.length === 0) {
                newItems = [...currentItems];
            } else {
                const newComingItems = newListState.items.filter(
                    (item) => !currentItems.find((i) => i.value === item.value),
                );
                newItems = [...currentItems, ...newComingItems];
            }
        } else {
            if (newListState) {
                newItems = [...newListState.items];
            } else {
                newItems = [];
            }
        }
        const nextHasMoreItems =
            newListState.hasMoreItems ??
            newListState.items.length >= DEFAULT_LOAD_SIZE;
        if (!rawSort) {
            newItems.sort((a, b) => compareSearchListLabels(a.label, b.label));
        }

        newUpdateState = {
            items: newItems,
            after: newListState?.after,
            hasMoreItems: nextHasMoreItems,
        };
        updateFilter(filterKey, newUpdateState, false, subFilterKey);
    };

    const loadMoreItems = async () => {
        // if there's subFilterKey then it's a facet list, it does not support lazy loading
        if (
            !hasMoreItems ||
            !after ||
            subFilterKey ||
            isTyping ||
            searchInFlightRef.current
        ) {
            return;
        }
        setLoading(true);

        const generation = searchGenerationRef.current;
        aggregate({
            aggregateType: 'searchListLoadMore',
            aggregateFilter: filterKey as SearchListKeys,
            filterStates,
            aggregationsParams: {
                [SEARCH_LIST_AGG_PARAM_KEY_MAP[filterKey as SearchListKeys]]: {
                    after,
                    size: DEFAULT_LOAD_SIZE,
                    search,
                },
            },
            onAggregateStateChange: (request, state, data) =>
                handleAggregateStateChange(request, state, data, generation),
        });
    };

    const searchAggregate = (searchQuery: string) => {
        setIsTyping(false);
        setLoading(true);
        searchInFlightRef.current = true;
        searchGenerationRef.current += 1;
        const generation = searchGenerationRef.current;
        updateFilter(
            filterKey,
            {
                search: searchQuery,
                // Keep the current options on screen, but do not let
                // VirtualList page while the new query is in flight.
                hasMoreItems: false,
                after: undefined,
            },
            false,
            subFilterKey,
        );
        let aggregationsParams: SearchAggregationParams;
        if (subFilterKey) {
            aggregationsParams = {
                aggFacetOptions: [{ facet: subFilterKey, search: searchQuery }],
            };
        } else {
            aggregationsParams = {
                [SEARCH_LIST_AGG_PARAM_KEY_MAP[filterKey as SearchListKeys]]: {
                    after: undefined,
                    size: DEFAULT_LOAD_SIZE,
                    search: searchQuery,
                },
            };
        }
        aggregate({
            aggregateType: 'searchListSearch',
            aggregateFilter: filterKey as SearchListKeys,
            filterStates,
            aggregationsParams,
            onAggregateStateChange: (request, state, data) =>
                handleAggregateStateChange(request, state, data, generation),
        });
    };

    const { run: searchAggregateRun } = useDebounceFn(searchAggregate, {
        wait: 500,
    });

    // Scroll the list into view once on mount so filter options are visible
    // on mobile. Skip while the search input is focused — iOS dismisses the
    // keyboard when a focused field's ancestor is scrolled.
    useEffect(() => {
        if (!listRef.current || desktop) return;
        if (listRef.current.contains(document.activeElement)) return;
        listRef.current.scrollIntoView({
            block: 'center',
            behavior: 'smooth',
        });
        // Intentionally empty: do not re-scroll when `desktop` toggles.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isTyping) {
            setDraftSearch(search ?? '');
        }
    }, [search, isTyping]);

    return (
        <div className={css(S.FilterSearchList)} ref={listRef}>
            <SearchableList<FilterSearchListItem>
                {...restProps}
                items={items}
                inputValue={draftSearch}
                // Parent `loading` is true while the search aggregate is in
                // flight. Disabling a focused iOS field dismisses the keyboard.
                disabled={false}
                hasMoreItems={!!hasMoreItems && !isTyping && !loading}
                loadMoreItems={loadMoreItems}
                onSearchInputChange={(_e, data) => {
                    const next = data?.value ?? '';
                    setIsTyping(true);
                    setDraftSearch(next);
                    searchAggregateRun(next);
                }}
                variables={{
                    optionListWidth: pxToRem(320),
                }}
                noResult={
                    !loading && items.length === 0 && !!search && !isTyping
                        ? `No result for “${search}”`
                        : null
                }
            >
                {({ index, data }) => {
                    if (!data) return null;
                    return (
                        <Checkbox
                            design={{ paddingBottom: pxToRem(8) }}
                            indicator={{ kind: 'checkbox' }}
                            disabled={disabled}
                            onChange={(_, checkData) => {
                                const checked = !!checkData?.checked;
                                const newList = [...items];
                                const item = newList[index];
                                if (item) {
                                    item.checked = checked;
                                }

                                const newSelectedItems = selectedItems
                                    ? [...selectedItems]
                                    : [];
                                const selectedIndex =
                                    newSelectedItems.findIndex(
                                        (item) => item.value === data.value,
                                    );
                                if (selectedIndex !== -1) {
                                    if (!checked) {
                                        newSelectedItems.splice(
                                            selectedIndex,
                                            1,
                                        );
                                    }
                                } else {
                                    if (checked) {
                                        newSelectedItems.push(data);
                                    }
                                }
                                updateFilter(
                                    filterKey,
                                    {
                                        kind: 'SearchList',
                                        items: newList,
                                        selectedItems: newSelectedItems,
                                    },
                                    true,
                                    subFilterKey,
                                );
                            }}
                            label={data.label}
                            checked={
                                !!selectedItems?.find(
                                    (selectedItem) =>
                                        selectedItem.value === data.value,
                                )
                            }
                        />
                    );
                }}
            </SearchableList>
        </div>
    );
};

export default FilterSearchList;
